// Smart segments (spec §107): saved, explainable audiences. Each segment states its criteria,
// how missing data is handled (people whose data is incomplete are listed separately, never silently
// included or excluded), and is computed from permission-filtered local data at refresh time.
import type { WorkspaceView } from './changeset.ts';
import { addDays, ageOn, diffDays, startOfWeek, type Day } from './dates.ts';
import { latestFinalNeedsOf } from './household.ts';
import { childrenOfPerson, coverageForPerson, coveredCategories, familyPlanRecorded } from './person-cover.ts';
import { nameMatchesOn } from './namedays/index.ts';
import { insuredIds, policyCategory } from './policy-engine.ts';
import { renewalQueue } from './business.ts';
import { isActivePolicy, reviewStatus } from './rules.ts';
import type { Person, Policy, PolicyCategory } from './schema.ts';
import { getSetting } from './services.ts';
import { doNotCallKeys, isDoNotCall } from './calls.ts';
import { GAP_SEGMENTS, gapAreaOfSegment, protectionGapMap, type GapSegment } from './protection-gaps.ts';
import { taxOpportunity } from './tax-center.ts';
import { CROSS_SELL_SEGMENTS, crossSellHits, type CrossSellSegment } from './cross-sell.ts';

export const SEGMENT_KEYS = ['parents_children_under_10', 'parents_children_0_12', 'no_medical_recorded', 'mortgage_no_credit_life', 'life_no_savings', 'over_40_no_retirement', 'reviews_overdue', 'birthdays_this_month', 'namedays_this_week', 'tax_opportunity', 'lapsed_policies', 'gi_renewals_30', 'life_no_medical', 'parents_no_education',
  // idea 45: life ↔ general cross-sell from insured items (cross-sell.ts)
  ...CROSS_SELL_SEGMENTS,
  // v12 (idea 38): the protection-gap map's information campaigns (protection-gaps.ts)
  ...GAP_SEGMENTS] as const;
export type SegmentKey = typeof SEGMENT_KEYS[number];
export interface SegmentMember { personId: string; detail: Record<string, string | number | boolean> }
export interface SegmentResult { key: SegmentKey; members: SegmentMember[]; incomplete: SegmentMember[]; refreshedAt: Day; population: number }

const people = (v: WorkspaceView) => v.rows('persons').filter((p) => !p.archivedAt && p.stage !== 'dependent');
const clients = (v: WorkspaceView) => people(v).filter((p) => p.stage === 'client');
/** the categories the person is covered for today, as an insured person (coverageForPerson) */
const coverCats = (v: WorkspaceView, p: Person, today: Day): Set<PolicyCategory> => coveredCategories(coverageForPerson(v, p.id, today));
const hasCat = (cats: Set<PolicyCategory>, want: PolicyCategory[]) => want.some((c) => cats.has(c));
/** children from the household and the Family tab, once per person */
function childrenOf(v: WorkspaceView, p: Person, today: Day) {
  const kids = childrenOfPerson(v, p.id, today);
  return { all: kids, known: kids.filter((k) => k.birthDate).map((k) => ({ name: k.name, age: k.age!, birthDate: k.birthDate! })), unknown: kids.filter((k) => !k.birthDate).length };
}

export function runSegment(v: WorkspaceView, key: SegmentKey, today: Day): SegmentResult {
  const members: SegmentMember[] = [], incomplete: SegmentMember[] = [];
  if ((CROSS_SELL_SEGMENTS as readonly string[]).includes(key)) {
    // clients and prospects who have insured items; incomplete data (cover detail or employees not recorded) listed apart
    for (const h of crossSellHits(v, today, { segment: key as CrossSellSegment })) (h.incomplete ? incomplete : members).push({ personId: h.personId, detail: h.detail });
    return { key, members, incomplete, refreshedAt: today, population: people(v).filter((p) => v.where('insured_items', 'personId', p.id).some((i) => i.status === 'active')).length };
  }
  if ((GAP_SEGMENTS as readonly string[]).includes(key)) {
    const area = gapAreaOfSegment(key as GapSegment);
    const map = protectionGapMap(v, today);
    for (const r of map.rows) if (r.cells[area] === 'gap') members.push({ personId: r.personId, detail: { area, gaps: r.gaps } });
    return { key, members, incomplete, refreshedAt: today, population: map.rows.filter((r) => r.cells[area] !== 'na').length };
  }
  const base = key === 'gi_renewals_30' || key === 'lapsed_policies' ? people(v) : clients(v);
  for (const p of base) {
    const cats = coverCats(v, p, today);
    // tax relief follows the premiums of live policies the client holds or is insured on (unchanged; not a coverage test)
    const pols = v.rows('policies').filter((x) => isActivePolicy(x) && (x.personId === p.id || insuredIds(x).includes(p.id)));
    const needs = latestFinalNeedsOf(v, p.id);
    const needLine = (area: string) => (Array.isArray(needs?.areas) ? (needs!.areas as { area: string; existing?: number | null }[]).find((a) => a.area === area) : undefined);
    switch (key) {
      case 'parents_children_under_10':
      case 'parents_children_0_12': {
        const max = key === 'parents_children_under_10' ? 9 : 12;
        const c = childrenOf(v, p, today);
        const hit = c.known.filter((k) => k.age >= 0 && k.age <= max);
        if (hit.length) members.push({ personId: p.id, detail: { children: hit.length, youngest: Math.min(...hit.map((k) => k.age)) } });
        else if (c.unknown) incomplete.push({ personId: p.id, detail: { childrenWithoutBirthDate: c.unknown } });
        break;
      }
      case 'no_medical_recorded':
        if (!hasCat(cats, ['medical']) && !((needLine('health')?.existing ?? 0) > 0)) (needs ? members : incomplete).push({ personId: p.id, detail: { needsEvaluated: !!needs } });
        break;
      case 'life_no_medical':
        if (hasCat(cats, ['life', 'credit_life']) && !hasCat(cats, ['medical'])) members.push({ personId: p.id, detail: { needsEvaluated: !!needs } });
        break;
      case 'mortgage_no_credit_life': {
        const mortgage = v.where('financial_items', 'personId', p.id).find((i) => i.kind === 'liability' && i.type === 'mortgage') ?? ((needs?.profile as { mortgage?: number } | undefined)?.mortgage ? { balance: (needs!.profile as { mortgage: number }).mortgage } : undefined);
        if (mortgage && !hasCat(cats, ['credit_life'])) members.push({ personId: p.id, detail: { balance: mortgage.balance ?? 0 } });
        break;
      }
      case 'life_no_savings':
        if (hasCat(cats, ['life']) && !hasCat(cats, ['savings', 'pension', 'education'])) members.push({ personId: p.id, detail: {} });
        break;
      case 'over_40_no_retirement':
        if (!p.birthDate) { if (!hasCat(cats, ['pension'])) incomplete.push({ personId: p.id, detail: { birthDate: false } }); break; }
        if (ageOn(p.birthDate, today) >= 40 && !hasCat(cats, ['pension'])) members.push({ personId: p.id, detail: { age: ageOn(p.birthDate, today) } });
        break;
      case 'parents_no_education': {
        const c = childrenOf(v, p, today);
        if ((c.known.length || c.unknown) && !familyPlanRecorded(v, p.id, today, 'education', c.all) && !v.where('financial_items', 'personId', p.id).some((i) => i.kind === 'goal' && i.type === 'education')) members.push({ personId: p.id, detail: { children: c.known.length + c.unknown } });
        break;
      }
      case 'reviews_overdue': {
        const rs = reviewStatus(p, v.where('policies', 'personId', p.id), today);
        if (rs.state === 'overdue' || rs.state === 'never') members.push({ personId: p.id, detail: { state: rs.state, days: rs.daysOverdue } });
        break;
      }
      case 'birthdays_this_month': {
        const m = today.slice(5, 7);
        const own = p.birthDate?.slice(5, 7) === m;
        const kids = childrenOf(v, p, today).known.filter((k) => k.birthDate.slice(5, 7) === m);
        if (own || kids.length) members.push({ personId: p.id, detail: { own, children: kids.length } });
        break;
      }
      case 'namedays_this_week': {
        const first = startOfWeek(today, 1);
        for (let i = 0; i < 7; i++) {
          const d = addDays(first, i);
          const feast = nameMatchesOn(d).get((p.firstName ?? p.displayName.split(' ')[0] ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase());
          if (feast) { members.push({ personId: p.id, detail: { date: d, feast } }); break; }
        }
        break;
      }
      case 'tax_opportunity': {
        // a saved calculation (payslip or Tax Center, this year or last) is the best evidence; otherwise the needs-analysis income
        const snaps = v.where('tax_snapshots', 'personId', p.id).filter((x) => x.taxYear >= Number(today.slice(0, 4)) - 1);
        const snap = snaps.reduce<typeof snaps[number] | null>((a, x) => (!a || x.createdAt > a.createdAt || (x.createdAt === a.createdAt && x.id > a.id) ? x : a), null);
        if (snap) {
          const cap = Number((snap.result as { remainingCapacity?: number }).remainingCapacity ?? 0);
          if (cap > 0) members.push({ personId: p.id, detail: { capacity: cap, status: snap.status, source: String(snap.notes ?? '').startsWith('payslip') ? 'payslip' : 'calculation' } });
          break;
        }
        const income = (needs?.profile as { annualIncome?: number } | undefined)?.annualIncome ?? null;
        if (!income) { incomplete.push({ personId: p.id, detail: { income: false } }); break; }
        const region = getSetting<string>(v, 'region', 'CY');
        const r = taxOpportunity(v, region, Number(today.slice(0, 4)), { grossIncome: income, lifePolicies: pols.filter((x) => x.branch === 'life').map((x) => ({ annualPremium: x.annualPremium, sumInsured: x.sumAssured ?? null })) });
        if (r.available && r.opportunity.remainingCapacity > 0) members.push({ personId: p.id, detail: { capacity: r.opportunity.remainingCapacity, status: r.opportunity.status } });
        break;
      }
      case 'lapsed_policies': {
        const lapsed = v.where('policies', 'personId', p.id).filter((x) => x.status === 'lapsed');
        if (lapsed.length) members.push({ personId: p.id, detail: { policies: lapsed.length } });
        break;
      }
      case 'gi_renewals_30':
        break;
    }
  }
  if (key === 'gi_renewals_30') {
    const seen = new Set<string>();
    for (const r of renewalQueue(v, today)) if (r.policy.branch === 'general' && r.days >= 0 && r.days <= 30 && !seen.has(r.policy.personId)) { seen.add(r.policy.personId); members.push({ personId: r.policy.personId, detail: { dueDate: r.dueDate, days: r.days } }); }
  }
  return { key, members, incomplete, refreshedAt: today, population: base.length };
}

// --- audience eligibility (consent, suppression, channel) -------------------------------------------------
export type Channel = 'email' | 'sms' | 'whatsapp' | 'phone' | 'letter';
export type Ineligible = 'no_consent' | 'consent_withdrawn' | 'channel_not_consented' | 'suppressed' | 'no_contact_detail' | 'archived';
export interface AudienceEntry { personId: string; eligible: boolean; reason: Ineligible | null }
/** latest consent for a purpose (a later withdrawal always wins) */
export function consentState(v: WorkspaceView, personId: string, purposes: string[] = ['marketing', 'newsletter']) {
  const list = v.where('consents', 'personId', personId).filter((c) => purposes.includes(c.purpose)).sort((a, b) => (a.date ?? a.createdAt).localeCompare(b.date ?? b.createdAt) || a.createdAt.localeCompare(b.createdAt));
  const last = list[list.length - 1];
  return { recorded: !!last, granted: !!last && last.granted && !last.withdrawnAt, channel: consentChannel(last?.channel), withdrawn: !!last && (!last.granted || !!last.withdrawnAt) };
}
const CHANNELS: readonly Channel[] = ['email', 'sms', 'whatsapp', 'phone', 'letter'];
/** A consent's channel restricts contact only when it names a real channel; free text recorded by older
 *  versions in that field (e.g. "signed form" — really the source of the consent) is not a restriction. */
export function consentChannel(raw: string | null | undefined): Channel | null {
  const c = (raw ?? '').trim().toLowerCase().replace(/^e-mail$/, 'email').replace(/^(telephone|call|τηλέφωνο)$/, 'phone');
  return (CHANNELS as readonly string[]).includes(c) ? (c as Channel) : null;
}
export function audienceFor(v: WorkspaceView, personIds: readonly string[], channel: Channel): AudienceEntry[] {
  const requireConsent = getSetting<boolean>(v, 'campaign.requireConsent', true);
  const suppression = new Set(getSetting<string[]>(v, 'marketing.suppression', []).map((x) => x.toLowerCase()));
  const dnc = doNotCallKeys(v);
  return [...new Set(personIds)].map((id) => {
    const p = v.get('persons', id);
    if (!p || p.archivedAt) return { personId: id, eligible: false, reason: 'archived' as const };
    if (suppression.has(p.id) || (p.primaryEmail && suppression.has(p.primaryEmail.toLowerCase())) || (p.primaryPhone && suppression.has(p.primaryPhone.replace(/\s+/g, ''))) || (p.data as { doNotContact?: boolean } | undefined)?.doNotContact) return { personId: id, eligible: false, reason: 'suppressed' as const };
    // v12: «do not call» (on the person, or the same number marked in a prospect list) is honoured for campaigns by phone call
    if (channel === 'phone' && isDoNotCall(v, { personId: p.id, phone: p.primaryPhone }, dnc)) return { personId: id, eligible: false, reason: 'suppressed' as const };
    if (requireConsent) {
      const c = consentState(v, id);
      if (c.withdrawn) return { personId: id, eligible: false, reason: 'consent_withdrawn' as const };
      if (!c.granted) return { personId: id, eligible: false, reason: 'no_consent' as const };
      if (c.channel && c.channel !== channel && !(c.channel === 'phone' && (channel === 'sms' || channel === 'whatsapp'))) return { personId: id, eligible: false, reason: 'channel_not_consented' as const };
    }
    const contact = channel === 'email' ? p.primaryEmail : channel === 'letter' ? p.address : p.primaryPhone ?? p.whatsapp;
    if (!contact) return { personId: id, eligible: false, reason: 'no_contact_detail' as const };
    return { personId: id, eligible: true, reason: null };
  });
}
export const daysSince = (from: Day, to: Day) => diffDays(from, to);
